import { chain, find, flatMap, last } from "lodash";
import { Descendant, Text } from "slate";

import type { AtlasTextEditorVariableData } from "../types";

const encode = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    // Replace a handful of special space characters and combinations which
    // generally warrant a non-breaking space
    .replace(/ /g, "&nbsp;")
    .replace(/ \|/g, "&nbsp;|")
    .replace(/ -/g, "&nbsp;-")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const serialize = (
  nodeOrNodes: Descendant[] | Descendant,
  availablePlaceholders: AtlasTextEditorVariableData[] = []
): string => {
  if (Array.isArray(nodeOrNodes)) {
    return flatMap(nodeOrNodes, (node: Descendant) =>
      serialize(node, availablePlaceholders)
    ).join("");
  }
  const node = nodeOrNodes;

  if (Text.isText(node)) {
    let string = encode(node.text);
    // If we don't have any actual string value, we should ignore any text
    // modifiers
    if (string === "") {
      return string;
    }

    let style = "";
    if (node["text-color"]) {
      style = `color:${node["text-color"]};`;
    }
    if (node["bg-color"]) {
      style = `${style}background-color:${node["bg-color"]};`;
    }
    if (node.underline) {
      if (style) {
        string = `<u style="${style}">${string}</u>`;
      } else {
        string = `<u>${string}</u>`;
      }
    }
    if (node.bold) {
      if (style) {
        string = `<strong style="${style}">${string}</strong>`;
      } else {
        string = `<strong>${string}</strong>`;
      }
    }
    if (node.italic) {
      if (style) {
        string = `<em style="${style}">${string}</em>`;
      } else {
        string = `<em>${string}</em>`;
      }
    }
    if (style && !node.bold && !node.italic && !node.underline) {
      string = `<span style="${style}">${string}</span>`;
    }
    return string.replace(/\n/g, "<br>");
  }

  const children = chain(node.children)
    .map((child: Descendant) => serialize(child, availablePlaceholders))
    .reduce((acc: string[], value: string, index: number) => {
      if (index === 0) {
        return acc.concat(value);
      }

      const previous = last(acc);
      const firstDocument = new DOMParser().parseFromString(
        previous!,
        "text/html"
      ).body;
      const secondDocument = new DOMParser().parseFromString(
        value,
        "text/html"
      ).body;
      if (firstDocument.children.length && secondDocument.children.length) {
        const previousElement = firstDocument.lastElementChild as HTMLElement;
        const thisElement = secondDocument.firstElementChild as HTMLElement;
        if (previousElement.nodeName === thisElement.nodeName) {
          // Don't try
          if (
            previousElement.getAttribute("style") !==
            thisElement.getAttribute("style")
          ) {
            return acc.concat(value);
          }
          const nodeName = previousElement.nodeName.toLowerCase();

          if (nodeName === "li" || nodeName === "a" || nodeName === "br") {
            return acc.concat(value);
          }

          if (nodeName === "span" || nodeName === "div") {
            return acc.concat(value);
          }
          // If we haven't bailed yet, we can and should collapse this node
          // with its sibling
          acc.pop();
          const replacedPrevious = previous!.replace(
            new RegExp(`</${nodeName}>$`),
            ""
          );
          const replacedValue = value.replace(
            new RegExp(`<${nodeName}[^>]*>`),
            ""
          );
          const space =
            previousElement.innerHTML.endsWith(" ") ||
            thisElement.innerHTML.startsWith(" ") ||
            (!previousElement.innerText && !thisElement.innerText)
              ? ""
              : " ";
          return acc.concat([`${replacedPrevious}${space}${replacedValue}`]);
        }
      }
      return acc.concat(value);
    }, [])
    .join("")
    .value();

  switch (node.type) {
    case "paragraph":
      if (children === "") {
        return "<div><br></div>";
      }
      return `<div>${children}</div>`;
    case "numbered-list":
      return `<ol>${children}</ol>`;
    case "bulleted-list":
      return `<ul>${children}</ul>`;
    case "list-item":
      return `<li>${children}</li>`;
    case "link": {
      let style = "";
      if (node["text-color"]) {
        style = `color:${node["text-color"]};`;
      }
      if (node["bg-color"]) {
        style = `${style}background-color:${node["bg-color"]};`;
      }
      if (style) {
        return `<a href="${encode(
          node.url
        )}" rel="noopener noreferrer" target="_blank" style="${style}">${children}</a>`;
      }
      return `<a href="${encode(
        node.url
      )}" rel="noopener noreferrer" target="_blank">${children}</a>`;
    }
    case "variable": {
      const variable = `<span class="ql-placeholder-content" data-id="${
        node.id
      }" data-label="${
        find(availablePlaceholders, { id: node.id })?.name
      }" spellcheck="false"></span>`;
      let varStyle = "";
      if (node.children?.[0]["text-color"]) {
        varStyle = `color:${node.children?.[0]["text-color"]};`;
      }
      if (node.children?.[0]["bg-color"]) {
        varStyle = `${varStyle}background-color:${node.children?.[0]["bg-color"]};`;
      }
      let final = "";
      if (node.children?.[0].underline) {
        if (varStyle) {
          final = `<u style="${varStyle}">${variable}</u>`;
        } else {
          final = `<u>${variable}</u>`;
        }
      }
      if (node.children?.[0].bold) {
        if (final) {
          final = `<strong>${final}</strong>`;
        } else if (varStyle) {
          final = `<strong style="${varStyle}">${variable}</strong>`;
        } else {
          final = `<strong>${variable}</strong>`;
        }
      }
      if (node.children?.[0].italic) {
        if (final) {
          final = `<em>${final}</em>`;
        } else if (varStyle) {
          final = `<em style="${varStyle}">${variable}</em>`;
        } else {
          final = `<em>${variable}</em>`;
        }
      }
      if (final) {
        return final;
      }
      if (varStyle) {
        return `<span style="${varStyle}" class="ql-placeholder-content" data-id="${
          node.id
        }" data-label="${
          find(availablePlaceholders, { id: node.id })?.name
        }" spellcheck="false"></span>`;
      }
      return variable;
    }
    default:
      return children;
  }
};

export default serialize;
